import {
  Button,
  Col,
  Flex,
  Form,
  InputNumber,
  message,
  Modal,
  Row,
  Select,
  Typography,
} from "antd";
import { useEffect, useMemo, useState } from "react";
import { Redemption } from "../../../api/redemption/Redemption";
import {
  SalesSnapshot,
  SalesUpdateRequest,
} from "../../../api/redemption/Sales";
import {
  useGetSalesConfigQuery,
  usePutSalesMutation,
} from "../../../api/redemption/redemptionApi";
import {
  calculateSalesPayment,
  currencySymbol,
  isValidSalesAmount,
  SALES_AMOUNT_MIN,
  SALES_AMOUNT_VALIDATION_MESSAGE,
  SALES_PAYMENT_METHODS,
  SalesCurrency,
  SalesModeOfPayment,
} from "../manual/salesPayment";

type SalesFormValues = {
  currency?: SalesCurrency;
  modeOfPayment?: SalesModeOfPayment;
  amountPaid?: string;
};

type SalesTabProps = {
  orderId: string;
  sales?: Redemption;
  onSaved: () => void;
};

const SALES_FIELD_NAMES: (keyof SalesSnapshot)[] = [
  "Sales_Currency",
  "Sales_ModeOfPayment",
  "Sales_AmountPaid",
  "Sales_PercentageFee",
  "Sales_FixedFee",
  "Sales_ProcessingFee",
  "Sales_WithholdingTaxRate",
  "Sales_WithholdingTaxBasis",
  "Sales_CreditableWithholdingTax",
  "Sales_TotalDeductions",
  "Sales_NetAmountReceived",
];
const TAKE_APP_PAYMENT_METHODS: SalesModeOfPayment[] = [
  "GCash Business",
  "Bank Transfer",
];

const isEmptyValue = (value: unknown) => (
  value === undefined || value === null || value === ""
);

const hasSalesSnapshot = (sales?: Redemption) => (
  sales != null && SALES_FIELD_NAMES.some(field => sales[field] != null)
);

export default function SalesTab({ orderId, sales, onSaved }: SalesTabProps) {
  const [form] = Form.useForm<SalesFormValues>();
  const [messageApi, contextHolder] = message.useMessage();
  const [putSales, { isLoading }] = usePutSalesMutation();
  const {
    data: salesConfig,
    isFetching: isConfigLoading,
    isError: isConfigError,
  } = useGetSalesConfigQuery(orderId);
  const [hasSavedSales, setHasSavedSales] = useState(false);
  const [savedSales, setSavedSales] = useState<Partial<SalesSnapshot> | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const currency = Form.useWatch("currency", form);
  const modeOfPayment = Form.useWatch("modeOfPayment", form);
  const amountPaid = Form.useWatch("amountPaid", form);
  const isTakeApp = salesConfig?.takeApp === true;

  useEffect(() => {
    const hasSales = hasSalesSnapshot(sales);
    setHasSavedSales(hasSales);
    setSavedSales(hasSales ? sales ?? null : null);
    setIsDirty(false);
    form.setFieldsValue({
      currency: sales?.Sales_Currency ?? (isTakeApp ? "PHP" : undefined),
      modeOfPayment: sales?.Sales_ModeOfPayment
        ?? (isTakeApp ? "GCash Business" : undefined),
      amountPaid: sales?.Sales_AmountPaid == null
        ? undefined
        : String(sales.Sales_AmountPaid),
    });
  }, [form, isTakeApp, orderId, sales]);

  const calculation = useMemo(() => {
    if (!currency || !modeOfPayment || !isValidSalesAmount(amountPaid)) {
      return undefined;
    }
    try {
      return calculateSalesPayment(currency, modeOfPayment, Number(amountPaid));
    } catch {
      return undefined;
    }
  }, [amountPaid, currency, modeOfPayment]);

  const displayCalculation = calculation && {
    ...calculation,
    totalDeductions: !isDirty && savedSales?.Sales_TotalDeductions != null
      ? savedSales.Sales_TotalDeductions
      : calculation.totalDeductions,
    netAmountReceived: !isDirty && savedSales?.Sales_NetAmountReceived != null
      ? savedSales.Sales_NetAmountReceived
      : calculation.netAmountReceived,
  };

  const validateGroupField = (label: string, value: unknown) => {
    const values = [
      form.getFieldValue("currency"),
      form.getFieldValue("modeOfPayment"),
      form.getFieldValue("amountPaid"),
    ];
    if (values.some(field => !isEmptyValue(field)) && isEmptyValue(value)) {
      return Promise.reject(
        new Error(`${label} is required when Sales information is entered`));
    }
    return Promise.resolve();
  };

  const confirmClear = () => new Promise<boolean>(resolve => {
    Modal.confirm({
      title: "Clear Sales information?",
      content: `This will remove Sales information from every item in order ${orderId}.`,
      okText: "Clear Sales",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    });
  });

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const isClear = [
        values.currency,
        values.modeOfPayment,
        values.amountPaid,
      ].every(isEmptyValue);
      if (isClear && hasSavedSales && !(await confirmClear())) {
        return;
      }

      const request: SalesUpdateRequest = {
        orderId,
        currency: values.currency ?? null,
        modeOfPayment: values.modeOfPayment ?? null,
        amountPaid: values.amountPaid == null ? null : String(values.amountPaid),
      };
      const response = await putSales(request).unwrap();
      setSavedSales(response.sales);
      setHasSavedSales(response.sales != null);
      setIsDirty(false);
      messageApi.success(
        response.sales == null
          ? "Sales information cleared"
          : "Sales information saved");
      onSaved();
    } catch (error) {
      if ((error as { errorFields?: unknown }).errorFields) {
        return;
      }
      messageApi.error("Unable to save Sales information");
    }
  };

  const handleClear = async () => {
    if (!(await confirmClear())) {
      return;
    }
    try {
      const response = await putSales({
        orderId,
        currency: null,
        modeOfPayment: null,
        amountPaid: null,
      }).unwrap();
      setSavedSales(response.sales);
      setHasSavedSales(false);
      setIsDirty(false);
      form.setFieldsValue({
        currency: "PHP",
        modeOfPayment: "GCash Business",
        amountPaid: undefined,
      });
      messageApi.success("Sales information cleared");
      onSaved();
    } catch {
      messageApi.error("Unable to save Sales information");
    }
  };

  const formatAmount = (value: number) => (
    `${displayCalculation?.symbol}${value.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`
  );

  const hasInput = [currency, modeOfPayment, amountPaid]
    .some(value => !isEmptyValue(value));

  return (
    <Form
      form={form}
      layout="vertical"
      onValuesChange={() => setIsDirty(true)}
    >
      {contextHolder}
      <Row gutter={8}>
        <Col span={12}>
          <Form.Item
            name="currency"
            label="Currency"
            dependencies={["modeOfPayment", "amountPaid"]}
            rules={[{
              validator: (_, value) => validateGroupField("Currency", value),
            }]}
          >
            <Select
              allowClear={!isTakeApp}
              disabled={isConfigLoading || isTakeApp}
              placeholder="Select currency"
              options={[
                { label: "PHP", value: "PHP" },
                { label: "SGD", value: "SGD" },
              ]}
              onChange={() => form.setFieldValue("modeOfPayment", undefined)}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="modeOfPayment"
            label="Mode of Payment"
            dependencies={["currency", "amountPaid"]}
            rules={[{
              validator: (_, value) => validateGroupField("Mode of Payment", value),
            }]}
          >
            <Select
              allowClear
              disabled={isConfigLoading || !currency}
              placeholder="Select payment method"
              options={(isTakeApp
                ? TAKE_APP_PAYMENT_METHODS
                : (currency ? SALES_PAYMENT_METHODS[currency] : [])).map(method => ({
                label: method,
                value: method,
              }))}
            />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item
        name="amountPaid"
        label="Amount Paid"
        dependencies={["currency", "modeOfPayment"]}
        rules={[{
          validator: async (_, value) => {
            await validateGroupField("Amount Paid", value);
            if (!isEmptyValue(value) && !isValidSalesAmount(value)) {
              return Promise.reject(
                new Error(SALES_AMOUNT_VALIDATION_MESSAGE));
            }
            return Promise.resolve();
          },
        }]}
      >
        <InputNumber
          stringMode
          min={SALES_AMOUNT_MIN}
          precision={2}
          addonBefore={currency ? currencySymbol(currency) : undefined}
          style={{ width: "100%" }}
        />
      </Form.Item>
      {displayCalculation && (
        <Flex gap={8} vertical>
          <Flex justify="space-between">
            <Typography.Text>Total Deductions</Typography.Text>
            <Typography.Text>
              {formatAmount(displayCalculation.totalDeductions)}
            </Typography.Text>
          </Flex>
          <Flex justify="space-between">
            <Typography.Text strong>Net Amount Received</Typography.Text>
            <Typography.Text strong>
              {formatAmount(displayCalculation.netAmountReceived)}
            </Typography.Text>
          </Flex>
        </Flex>
      )}
      {!hasSavedSales && !hasInput && (
        <Typography.Text type="secondary">
          Sales information not recorded
        </Typography.Text>
      )}
      {isConfigError && (
        <Typography.Text type="danger">
          Unable to load Sales configuration
        </Typography.Text>
      )}
      <Flex justify="end" style={{ marginTop: 16 }}>
        {isTakeApp && hasSavedSales && (
          <Button
            danger
            loading={isLoading}
            disabled={isConfigError}
            onClick={handleClear}
            style={{ marginRight: 8 }}
          >
            Clear Sales
          </Button>
        )}
        <Button
          type="primary"
          loading={isLoading || isConfigLoading}
          disabled={isConfigError || (!hasInput && !hasSavedSales)}
          onClick={handleSave}
        >
          Save Sales
        </Button>
      </Flex>
    </Form>
  );
}
